import { useTranslation } from 'react-i18next';
import React from 'react';
import { Copy, Check, ClipboardList, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '../ui/label';

/**
 * A form Label paired with a tiny copy-to-clipboard button that
 * appears whenever `value` is non-empty. Perfect for one-click
 * pasting of frequently-used contact info, IDs, and banking data
 * into external systems.
 *
 * Props
 * - label     : string shown as the field label
 * - value     : raw value to copy (falsy values hide the button)
 * - format    : optional (value) => string; useful for Date -> YYYY-MM-DD
 * - testId    : optional data-testid on the copy button
 * - className : optional extra classes on the wrapper
 */
export default function CopyableLabel({ label, value, format, testId, className = '' }) {
  const [copied, setCopied] = React.useState(false);

  const rendered = React.useMemo(() => {
    if (value === null || value === undefined || value === '') return '';
    if (format) return format(value);
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return String(value);
  }, [value, format]);

  const hasValue = rendered.length > 0;

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(rendered);
      setCopied(true);
      toast.success(`Copied "${label}"`);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      toast.error(`Copy failed: ${err?.message || 'clipboard unavailable'}`);
    }
  };

  return (
    <div className={`flex items-center gap-1.5 ${className}`.trim()}>
      <Label>{label}</Label>
      {hasValue && (
        <button
          type="button"
          onClick={copy}
          title={`Copy ${label.toLowerCase()}`}
          data-testid={testId}
          className={
            'inline-flex items-center justify-center w-5 h-5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors ' +
            (copied ? 'text-emerald-600' : '')
          }
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

/**
 * Standalone copy button for cases where the label is owned by
 * another component (e.g. FormDatePicker owns its own Label). Same
 * clipboard mechanics as CopyableLabel.
 */export function CopyIconButton({ label, value, format, testId, className = '' }) {
  const [copied, setCopied] = React.useState(false);

  const rendered = React.useMemo(() => {
    if (value === null || value === undefined || value === '') return '';
    if (format) return format(value);
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return String(value);
  }, [value, format]);

  if (!rendered) return null;

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(rendered);
      setCopied(true);
      toast.success(`Copied "${label}"`);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      toast.error(`Copy failed: ${err?.message || 'clipboard unavailable'}`);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy ${label.toLowerCase()}`}
      data-testid={testId}
      className={
        'inline-flex items-center justify-center w-5 h-5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0 ' +
        (copied ? 'text-emerald-600' : '') +
        ' ' + className
      }
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

/**
 * Joins address pieces into one navigable line — the format a maps app
 * expects when you paste it into its search box.
 */
export const formatAddress = (...parts) =>
  parts
    .flat()
    .map((v) => (v === null || v === undefined ? '' : String(v).replace(/\s+/g, ' ').trim()))
    .filter(Boolean)
    .join(', ');

/**
 * One-tap copy of a whole address (street, city, postal code, country)
 * as a single line, ready to paste straight into Google Maps.
 */
export function CopyAddressButton({ parts = [], label = 'Address', testId, className = '' }) {
  return (
    <CopyIconButton label={label} value={formatAddress(parts)} testId={testId} className={className} />
  );
}

/**
 * Opens the address in a maps app — Google Maps on desktop, and the
 * phone's own maps app on mobile, since both honour this search URL.
 */
export function OpenInMapsButton({ parts = [], label = 'address', testId, className = '' }) {
  const address = formatAddress(parts);
  if (!address) return null;

  const open = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <button
      type="button"
      onClick={open}
      title={`Open ${label.toLowerCase()} in Maps`}
      data-testid={testId}
      className={
        'inline-flex items-center justify-center w-5 h-5 rounded text-slate-400 '
        + 'hover:text-[#AA0000] hover:bg-slate-100 transition-colors shrink-0 ' + className
      }
    >
      <MapPin className="w-3.5 h-3.5" />
    </button>
  );
}

/**
 * The pair of actions an address deserves: copy it as one navigable
 * line, or jump straight into Maps. `testId` names the copy button;
 * the Maps button reuses it with a `maps-` prefix.
 */
export function AddressActions({ parts = [], label = 'Address', testId, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-0.5 shrink-0 ${className}`.trim()}>
      <CopyAddressButton parts={parts} label={label} testId={testId} />
      <OpenInMapsButton parts={parts} label={label}
        testId={testId ? testId.replace(/^copy-/, 'maps-') : undefined} />
    </span>
  );
}

/**
 * Copies a whole record as a tidy text block, so the essentials can be
 * pasted into an email without opening the record at all.
 *
 * lines: [[label, value], ...] — empty values are skipped.
 */
export function CopyAllButton({ title, lines = [], testId, className = '' }) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const text = React.useMemo(() => (
    lines
      .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
      .map(([l, v]) => `${l}: ${String(v).trim()}`)
      .join('\n')
  ), [lines]);

  if (!text) return null;

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(title ? `${title}\n${text}` : text);
      setCopied(true);
      toast.success(t('auto.misc2.detailsCopied'));
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      toast.error(`Copy failed: ${err?.message || 'clipboard unavailable'}`);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={t('auto.misc2.copyAllDetails')}
      data-testid={testId}
      className={
        'inline-flex items-center justify-center w-7 h-7 rounded border border-slate-200 '
        + 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors shrink-0 '
        + (copied ? 'text-emerald-600 border-emerald-300 ' : '') + className
      }
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <ClipboardList className="w-3.5 h-3.5" />}
    </button>
  );
}
